import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NotificationStack, Spinner } from "../../components/ui";
import { useCoreContext } from "../../context/global/CoreContext";
import {
  CollapsibleStatsBar,
  ManagementTable,
  useManagementRows,
} from "../../features/objectManagement";
import { buildWorkScopeColumns } from "../../features/office/workScopes/workScopeColumns";
import {
  mergeWorkScopeDraft,
  workScopeCreatePayload,
} from "../../features/office/workScopes/workScopeDraft";
import { buildWorkScopeStats } from "../../features/office/workScopes/workScopeStats";
import { useNotificationQueue } from "../../hooks/useNotificationQueue";
import { getUnits } from "../../services/api/dispatch";
import {
  createWorkScopeRecord,
  formatApiError,
  listWorkScopes,
  updateWorkScopeRecord,
} from "../../services/api/workScopes";

const SECTIONS = [
  {
    id: "active",
    label: "Active",
    defaultExpanded: true,
    // The unsaved row stays here until it is posted, then lands in its real section.
    match: (row) => Boolean(row._draft) || Boolean(row.active),
  },
  {
    id: "inactive",
    label: "Inactive",
    defaultExpanded: false,
    match: (row) => !row._draft && !row.active,
  },
];

/**
 * Blank row shown at the top of Active until it is saved or discarded.
 *
 * @returns {Object}
 */
function createEmptyDraft() {
  return {
    id: "draft",
    _draft: true,
    title: "",
    unit: null,
    active: true,
    planning_usage_count: 0,
    created: null,
  };
}

/**
 * Office page for viewing and inline-editing the work scope catalog.
 *
 * @returns {JSX.Element}
 */
export default function WorkScopeManagement() {
  const { appName, syncCatalogScope } = useCoreContext();
  const { items, push, dismiss } = useNotificationQueue();
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const draftRef = useRef(null);
  const creatingRef = useRef(false);
  const unitsRef = useRef([]);
  const mountedRef = useRef(true);
  const syncCatalogScopeRef = useRef(syncCatalogScope);
  syncCatalogScopeRef.current = syncCatalogScope;

  useEffect(() => {
    document.title = "Work Scope Management";
    if (appName) {
      appName.current = "Work Scope Management";
    }
  }, [appName]);

  /**
   * Load the full catalog and the unit dropdown.
   *
   * @returns {Promise<void>}
   */
  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [scopeRows, unitRows] = await Promise.all([
        listWorkScopes(),
        getUnits(),
      ]);
      setRows(scopeRows);
      setUnits(unitRows);
      unitsRef.current = unitRows;
    } catch (error) {
      setLoadError(
        formatApiError(error, "Could not load work scopes.")
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Persist one cell edit and keep the shared scope dropdown in step.
   *
   * @param {Object} _row - Row before the patch.
   * @param {Object} patch - Fields to write.
   * @returns {Promise<Object>} Saved row.
   */
  const handleSave = useCallback(
    async (_row, patch) => {
      const saved = await updateWorkScopeRecord(_row.id, patch);
      setRows((current) =>
        current.map((item) => (item.id === saved.id ? saved : item))
      );
      syncCatalogScope(saved);
      return saved;
    },
    [syncCatalogScope]
  );

  /**
   * Keep a unit primary key as `{id, name}` on the row while the save is in flight.
   *
   * @param {Object} row - Row being edited.
   * @param {Object} patch - Fields to write.
   * @returns {Object}
   */
  const mergeRow = useCallback(
    (row, patch) => mergeWorkScopeDraft(row, patch, unitsRef.current),
    []
  );

  const { rows: tableRows, saveRow, isRowSaving } = useManagementRows(
    rows,
    handleSave,
    mergeRow
  );

  const columns = useMemo(() => buildWorkScopeColumns(units), [units]);
  const stats = useMemo(() => buildWorkScopeStats(tableRows), [tableRows]);

  /**
   * Show a save failure. The table restores the previous cell value.
   *
   * @param {Object} row - Row being edited.
   * @param {Object} patch - Fields to write.
   * @returns {Promise<void>}
   */
  const commitRow = useCallback(
    async (row, patch) => {
      try {
        await saveRow(row, patch);
      } catch (error) {
        push(formatApiError(error, "Could not save this work scope."), "error");
      }
    },
    [push, saveRow]
  );

  /**
   * Insert the unsaved row at the top of Active.
   *
   * @returns {void}
   */
  const startDraft = () => {
    if (draftRef.current || creatingRef.current) return;
    const next = createEmptyDraft();
    draftRef.current = next;
    setDraft(next);
  };

  /**
   * Keep draft edits in a ref so leaving the row posts the latest values.
   *
   * @param {Object} patch - Fields changed in the draft.
   * @returns {void}
   */
  const handleDraftChange = (patch) => {
    const next = mergeWorkScopeDraft(draftRef.current, patch, unitsRef.current);
    draftRef.current = next;
    setDraft(next);
  };

  /**
   * Drop the unsaved row.
   *
   * @returns {void}
   */
  const cancelDraft = () => {
    draftRef.current = null;
    setDraft(null);
  };

  /**
   * Create the draft when focus leaves it, or discard it when the title is blank.
   *
   * @returns {Promise<void>}
   */
  const finishDraft = async () => {
    const current = draftRef.current;
    if (!current || creatingRef.current) return;
    const title = String(current.title || "").trim();
    if (!title) {
      cancelDraft();
      return;
    }

    creatingRef.current = true;
    setIsCreating(true);
    try {
      const saved = await createWorkScopeRecord(workScopeCreatePayload(current));
      draftRef.current = null;
      syncCatalogScope(saved);
      if (!mountedRef.current) return;
      setRows((existing) => [...existing, saved]);
      setDraft(null);
    } catch (error) {
      if (!mountedRef.current) return;
      push(formatApiError(error, "Could not add this work scope."), "error");
    } finally {
      creatingRef.current = false;
      if (mountedRef.current) {
        setIsCreating(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const current = draftRef.current;
      if (!current || creatingRef.current) return;
      // Blur may not run before a route change. Post a titled draft anyway.
      if (!String(current.title || "").trim()) return;
      creatingRef.current = true;
      createWorkScopeRecord(workScopeCreatePayload(current))
        .then((saved) => {
          syncCatalogScopeRef.current(saved);
        })
        .catch(() => {});
    };
  }, []);

  return (
    <main className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <NotificationStack items={items} onDismiss={dismiss} />
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-4 sm:px-5 lg:px-6">
        <header className="rounded-lg bg-white px-4 py-3 shadow-md shadow-gray-300">
          <h1 className="text-xl font-bold tracking-tight text-gray-950">
            Work Scope Management
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Edit and curate the work scope catalog.
          </p>
        </header>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="h-12 w-12" className="text-green-900" />
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p>{loadError}</p>
            <button
              type="button"
              className="mt-2 font-semibold underline"
              onClick={load}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <CollapsibleStatsBar
              foldedMetrics={stats.folded}
              expandedDetails={stats.expandedDetails}
            />
            <ManagementTable
              columns={columns}
              rows={tableRows}
              sections={SECTIONS}
              defaultSort={{ key: "count", direction: "desc" }}
              onCommit={commitRow}
              isRowSaving={(rowId) => isRowSaving(rowId) || (rowId === "draft" && isCreating)}
              searchPlaceholder="Search work scopes"
              emptyMessage="No matching work scopes."
              draftRow={draft}
              onDraftChange={handleDraftChange}
              onDraftFinish={finishDraft}
              onDraftCancel={cancelDraft}
              toolbar={
                <button
                  type="button"
                  className="text-sm rounded-md bg-green-50 border border-green-900/10 hover:bg-green-500/20 p-1 px-4 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                  disabled={Boolean(draft) || isCreating}
                  onClick={startDraft}
                >
                  + Work scope
                </button>
              }
            />
          </>
        )}
      </div>
    </main>
  );
}
